import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Building2, MapPin, Calendar, Clock, Check, X, FileText, Users, ChevronLeft, ChevronRight, Loader2, Trash2, Edit, Download, Upload, BarChart3, FileSpreadsheet, DollarSign, LogIn, LogOut, AlertCircle, CheckCircle, RotateCcw, Timer, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company options
const COMPANIES = [
  'Arbe Minimart',
  "Nenen's Agrivet Supply",
  "Nenen's Poultry",
  'Waga Internet Services',
  "Nenen's Integrated Farm"
];

// Verifiers and Approvers
const VERIFIERS = ['Alma', 'Jelyn', 'Arbe'];
const APPROVERS = ['Pabs', 'Arbe'];

const TimesheetManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState([]);
  const [currentTimesheet, setCurrentTimesheet] = useState(null);
  const [todayStatus, setTodayStatus] = useState({ in: false, out: false });
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [activeTab, setActiveTab] = useState('my-timesheet');
  const [hoursSummary, setHoursSummary] = useState([]);
  const [pendingOvertime, setPendingOvertime] = useState([]);
  const [payrollPreview, setPayrollPreview] = useState(null);
  const [loadingPayrollPreview, setLoadingPayrollPreview] = useState(false);
  
  // Search for All Timesheets tab
  const [timesheetSearch, setTimesheetSearch] = useState('');
  
  // Report data
  const [reportData, setReportData] = useState({ report: [], can_see_late: false });
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Create timesheet dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    company_name: '',
    location: '',
    name: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Sign in/out dialog
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [signAction, setSignAction] = useState(null);
  const [signing, setSigning] = useState(false);
  const [currentEntryStatus, setCurrentEntryStatus] = useState(null); // Track current status for display
  const [entryNotes, setEntryNotes] = useState(''); // Notes for the entry (work done, overtime reason, etc.)
  
  // Overtime request dialog - REMOVED (simplified to just notes on entry)
  
  // Quick Clock In/Out popup
  const [showClockPopup, setShowClockPopup] = useState(false);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  
  // Admin edit dialog
  const [showAdminEditDialog, setShowAdminEditDialog] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [editingEntry, setEditingEntry] = useState({ 
    clock_in_time: '', 
    clock_out_time: '', 
    day_off: false, 
    absent: false, 
    half_day: false 
  });
  const [saving, setSaving] = useState(false);
  
  // Edit timesheet details dialog (company, location, name)
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false);
  const [editDetailsForm, setEditDetailsForm] = useState({ company_name: '', location: '', employee_name: '' });
  const [editingTimesheetDetails, setEditingTimesheetDetails] = useState(null);
  
  // CA Deduction dialog
  const [showCADeductDialog, setShowCADeductDialog] = useState(false);
  const [caDeductEmployee, setCADeductEmployee] = useState(null);
  const [caDeductAmounts, setCADeductAmounts] = useState({});
  const [processingCADeduct, setProcessingCADeduct] = useState(false);
  
  // Cash Advance balances per user_id (for All Timesheets view)
  const [caBalances, setCaBalances] = useState({});
  
  // Import/Export
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importingBiometric, setImportingBiometric] = useState(false);
  
  // Track selected timesheet for continuous editing
  const [selectedTimesheetId, setSelectedTimesheetId] = useState(null);
  
  // Month navigation for cut-off - Simple 1-15 and 16-EOM pattern
  const [viewDate, setViewDate] = useState(new Date());
  // Period: 'first' (1-15) or 'second' (16-end)
  const [viewHalf, setViewHalf] = useState(() => {
    const day = new Date().getDate();
    return day <= 15 ? 'first' : 'second';
  });

  // Permission checks
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  
  const canEditTimesheets = isAdmin || username.includes('arbe') || fullName.includes('arbe');
  
  const isVerifier = isAdmin || 
                     username.includes('arbe') || fullName.includes('arbe') ||
                     username.includes('alma') || username.includes('jelyn') || 
                     fullName.includes('alma') || fullName.includes('jelyn');
  
  const isApprover = isAdmin || 
                     username.includes('arbe') || username.includes('pabs') || 
                     username.includes('pablito') || fullName.includes('arbe') || 
                     fullName.includes('pabs') || fullName.includes('pablito');
  
  const canManagePayslip = username.includes('arbe') || username.includes('alma') || 
                           username.includes('pabs') || username.includes('jelyn') || 
                           fullName.includes('arbe') || fullName.includes('alma') || 
                           fullName.includes('pabs') || fullName.includes('jelyn') || isAdmin;

  const canViewAllTimesheets = username.includes('arbe') || username.includes('alma') || 
                               username.includes('pabs') || username.includes('jelyn') ||
                               fullName.includes('arbe') || fullName.includes('alma') || 
                               fullName.includes('pabs') || fullName.includes('jelyn') || isAdmin;

  const todayStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  // Calculate current cut-off period - simple 1-15 and 16-end of month
  const getCutoffPeriod = (baseDate, half) => {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const monthName = baseDate.toLocaleString('en-US', { month: 'long' });
    
    if (half === 'first') {
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month, 15),
        label: `${monthName} 1-15, ${year}`,
        period_start: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        period_end: `${year}-${String(month + 1).padStart(2, '0')}-15`,
        days: Array.from({ length: 15 }, (_, i) => i + 1)
      };
    } else {
      return {
        start: new Date(year, month, 16),
        end: new Date(year, month, lastDay),
        label: `${monthName} 16-${lastDay}, ${year}`,
        period_start: `${year}-${String(month + 1).padStart(2, '0')}-16`,
        period_end: `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`,
        days: Array.from({ length: lastDay - 15 }, (_, i) => i + 16)
      };
    }
  };

  const cutoffPeriod = useMemo(() => getCutoffPeriod(viewDate, viewHalf), [viewDate, viewHalf]);

  useEffect(() => {
    fetchData();
  }, [cutoffPeriod.period_start]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReport();
    }
  }, [activeTab, cutoffPeriod.period_start]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [timesheetsRes, attendanceRes, hoursRes] = await Promise.all([
        axios.get(`${API}/timesheets-v2?period_start=${cutoffPeriod.period_start}&period_end=${cutoffPeriod.period_end}`),
        axios.get(`${API}/timesheets-v2/today-attendance`),
        axios.get(`${API}/timeclock/hours-summary?period_start=${cutoffPeriod.period_start}&period_end=${cutoffPeriod.period_end}`)
      ]);
      
      setTimesheets(timesheetsRes.data || []);
      setTodayAttendance(attendanceRes.data || []);
      setHoursSummary(hoursRes.data || []);
      
      // Fetch CA balances for all employees in this period (parallel)
      const uniqueUserIds = Array.from(new Set((timesheetsRes.data || []).map(ts => ts.user_id).filter(Boolean)));
      if (uniqueUserIds.length > 0) {
        Promise.all(uniqueUserIds.map(uid =>
          axios.get(`${API}/cash-advance/user/${uid}/total-balance`)
            .then(r => [uid, r.data])
            .catch(() => [uid, null])
        )).then(results => {
          const map = {};
          results.forEach(([uid, data]) => { if (data) map[uid] = data; });
          setCaBalances(map);
        });
      } else {
        setCaBalances({});
      }
      
      const myTimesheet = timesheetsRes.data?.find(ts => ts.user_id === user?.id);
      setCurrentTimesheet(myTimesheet || null);
      
      if (myTimesheet) {
        const today = new Date().toISOString().split('T')[0];
        const todayEntry = myTimesheet.entries?.find(e => e.date === today);
        setTodayStatus({
          in: todayEntry?.clock_in || false,
          out: todayEntry?.clock_out || false
        });
      }
      
      // Fetch pending overtime if user can approve
      const username = user?.username?.toLowerCase() || '';
      const canApprove = user?.role === 'admin' || ['arbe', 'alma', 'pabs', 'jelyn'].includes(username);
      if (canApprove) {
        try {
          const overtimeRes = await axios.get(`${API}/timeclock/overtime/pending`);
          setPendingOvertime(overtimeRes.data || []);
        } catch (err) {
          console.log('Could not fetch pending overtime');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load timesheet data');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch payroll preview (only for admins)
  const fetchPayrollPreview = async () => {
    const username = user?.username?.toLowerCase() || '';
    const fullName = user?.full_name?.toLowerCase() || '';
    const isAdmin = user?.role === 'admin';
    const canView = isAdmin || username.includes('arbe') || fullName.includes('arbe') || 
                    username.includes('alma') || fullName.includes('alma') ||
                    username.includes('pabs') || fullName.includes('pabs');
    
    if (!canView) return;
    
    setLoadingPayrollPreview(true);
    try {
      const res = await axios.get(`${API}/payroll/preview?period_start=${cutoffPeriod.period_start}&period_end=${cutoffPeriod.period_end}`);
      setPayrollPreview(res.data);
    } catch (error) {
      console.error('Error fetching payroll preview:', error);
      toast.error('Failed to load payroll preview');
    } finally {
      setLoadingPayrollPreview(false);
    }
  };

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/timesheets-v2/report?period_start=${cutoffPeriod.period_start}&period_end=${cutoffPeriod.period_end}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleCreateTimesheet = async () => {
    if (!createForm.company_name || !createForm.location || !createForm.name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/timesheets-v2`, {
        company_name: createForm.company_name,
        location: createForm.location,
        employee_name: createForm.name,
        period_start: cutoffPeriod.period_start,
        period_end: cutoffPeriod.period_end
      });
      
      toast.success('Timesheet created successfully!');
      setShowCreateDialog(false);
      setCreateForm({ company_name: '', location: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create timesheet');
    } finally {
      setCreating(false);
    }
  };

  const handleDateClick = (day, timesheet = currentTimesheet) => {
    if (!timesheet) {
      toast.error('Please create a timesheet first');
      return;
    }
    
    // Simple date string - no cross-month handling needed with 1-15 / 16-EOM pattern
    const dateStr = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    // Admin editing someone else's timesheet (can edit any date)
    if (canEditTimesheets && timesheet.user_id !== user?.id) {
      const entry = timesheet.entries?.find(e => e.date === dateStr);
      setSelectedTimesheetId(timesheet.id);
      setEditingTimesheet(timesheet);
      setEditingDate(dateStr);
      setEditingEntry({
        clock_in_time: entry?.clock_in_time || '',
        clock_out_time: entry?.clock_out_time || '',
        day_off: entry?.day_off ? true : false,
        absent: entry?.is_absent ? true : false,
        half_day: entry?.is_half_day ? true : false
      });
      setShowAdminEditDialog(true);
      return;
    }
    
    // For all employees (including themselves): only allow marking on today
    if (!isToday(day)) {
      toast.error('You can only mark attendance on the present day');
      return;
    }
    
    // Get current entry status to display in dialog
    const entry = timesheet.entries?.find(e => e.date === dateStr);
    setCurrentEntryStatus({
      hasIn: entry?.clock_in ? true : false,
      hasOut: entry?.clock_out ? true : false,
      isDayOff: entry?.day_off ? true : false,
      isAbsent: entry?.is_absent ? true : false,
      isHalfDay: entry?.is_half_day ? true : false
    });
    
    // Load existing notes for this entry
    setEntryNotes(entry?.notes || '');
    
    // Show sign dialog for everyone - they can choose In, Out, D, A, or H
    setSelectedDate(dateStr);
    setShowSignDialog(true);
  };

  const handleSignAction = async (action) => {
    if (action === 'choose') return;
    
    setSigning(true);
    try {
      await axios.post(`${API}/timesheets-v2/sign`, {
        timesheet_id: currentTimesheet.id,
        date: selectedDate,
        action: action,
        notes: entryNotes // Include notes with the sign action
      });
      
      const actionLabels = { in: 'In', out: 'Out', dayoff: 'D (Day Off)', absent: 'A (Absent)', halfday: 'H (Half Day)' };
      toast.success(`Marked as ${actionLabels[action]} successfully!`);
      setShowSignDialog(false);
      setEntryNotes(''); // Reset notes
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to mark ${action}`);
    } finally {
      setSigning(false);
    }
  };

  // Quick Clock In function
  const handleQuickClockIn = async () => {
    if (!currentTimesheet) {
      toast.error('No timesheet found. Please create one first.');
      return;
    }
    
    const today = new Date().toISOString().split('T')[0];
    
    setClockingIn(true);
    try {
      await axios.post(`${API}/timesheets-v2/sign`, {
        timesheet_id: currentTimesheet.id,
        date: today,
        action: 'in'
      });
      
      toast.success('Clocked IN successfully!');
      setShowClockPopup(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  // Quick Clock Out function
  const handleQuickClockOut = async () => {
    if (!currentTimesheet) {
      toast.error('No timesheet found. Please create one first.');
      return;
    }
    
    const today = new Date().toISOString().split('T')[0];
    
    setClockingOut(true);
    try {
      await axios.post(`${API}/timesheets-v2/sign`, {
        timesheet_id: currentTimesheet.id,
        date: today,
        action: 'out'
      });
      
      toast.success('Clocked OUT successfully!');
      setShowClockPopup(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  // Quick action for Half Day, Day Off, Absent
  const handleQuickAction = async (action) => {
    if (!currentTimesheet) {
      toast.error('No timesheet found. Please create one first.');
      return;
    }
    
    const today = new Date().toISOString().split('T')[0];
    const actionLabels = { halfday: 'Half Day', dayoff: 'Day Off', absent: 'Absent' };
    
    setSigning(true);
    try {
      await axios.post(`${API}/timesheets-v2/sign`, {
        timesheet_id: currentTimesheet.id,
        date: today,
        action: action
      });
      
      toast.success(`Marked as ${actionLabels[action]} successfully!`);
      setShowClockPopup(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to mark as ${actionLabels[action]}`);
    } finally {
      setSigning(false);
    }
  };

  // Overtime request functions removed - simplified to notes on entry

  const handleAdminEdit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/timesheets-v2/admin-edit`, {
        timesheet_id: editingTimesheet.id,
        date: editingDate,
        clock_in: editingEntry.clock_in_time ? true : false,
        clock_out: editingEntry.clock_out_time ? true : false,
        clock_in_time: editingEntry.clock_in_time || null,
        clock_out_time: editingEntry.clock_out_time || null,
        day_off: editingEntry.day_off,
        absent: editingEntry.absent,
        half_day: editingEntry.half_day
      });
      
      toast.success('Time entry updated successfully!');
      setShowAdminEditDialog(false);
      
      // Remember which timesheet we were editing to scroll back to it
      const editedTimesheetId = editingTimesheet.id;
      setSelectedTimesheetId(editedTimesheetId);
      
      // Stay on All Timesheets tab and refresh data
      setActiveTab('all-timesheets');
      await fetchData();
      
      // Scroll to the edited timesheet after a short delay
      setTimeout(() => {
        const element = document.querySelector(`[data-timesheet-id="${editedTimesheetId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-2', 'ring-blue-500');
          setTimeout(() => element.classList.remove('ring-2', 'ring-blue-500'), 2000);
        }
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update time entry');
    } finally {
      setSaving(false);
    }
  };

  const handleAdminRemove = async () => {
    if (!window.confirm(`Remove time entry for ${editingDate}?`)) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/timesheets-v2/admin-edit`, {
        timesheet_id: editingTimesheet.id,
        date: editingDate,
        clock_in: false,
        clock_out: false,
        clock_in_time: null,
        clock_out_time: null,
        day_off: false,
        absent: false,
        half_day: false
      });
      
      toast.success('Time entry removed!');
      setShowAdminEditDialog(false);
      
      // Remember which timesheet we were editing to scroll back to it
      const editedTimesheetId = editingTimesheet.id;
      setSelectedTimesheetId(editedTimesheetId);
      
      // Stay on All Timesheets tab and refresh data
      setActiveTab('all-timesheets');
      await fetchData();
      
      // Scroll to the edited timesheet after a short delay
      setTimeout(() => {
        const element = document.querySelector(`[data-timesheet-id="${editedTimesheetId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-2', 'ring-blue-500');
          setTimeout(() => element.classList.remove('ring-2', 'ring-blue-500'), 2000);
        }
      }, 300);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove time entry');
    } finally {
      setSaving(false);
    }
  };

  // Helper to scroll back to a specific timesheet card after data refresh
  const scrollToTimesheet = (timesheetId) => {
    if (!timesheetId) return;
    setSelectedTimesheetId(timesheetId);
    setActiveTab('all-timesheets');
    setTimeout(() => {
      const element = document.querySelector(`[data-timesheet-id="${timesheetId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.classList.add('ring-2', 'ring-blue-500');
        setTimeout(() => element.classList.remove('ring-2', 'ring-blue-500'), 2000);
      }
    }, 300);
  };

  const handleVerify = async (timesheetId, verifier) => {
    try {
      await axios.post(`${API}/timesheets-v2/${timesheetId}/verify`, { verified_by: verifier });
      toast.success('Timesheet verified!');
      await fetchData();
      scrollToTimesheet(timesheetId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify');
    }
  };

  const handleApprove = async (timesheetId, approver) => {
    try {
      await axios.post(`${API}/timesheets-v2/${timesheetId}/approve`, { approved_by: approver });
      toast.success('Timesheet approved!');
      await fetchData();
      scrollToTimesheet(timesheetId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleResetVerification = async (timesheetId) => {
    if (!window.confirm('Are you sure you want to reset this timesheet to draft? This will clear both verification and approval.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/timesheets-v2/${timesheetId}/reset-verification`);
      toast.success('Timesheet reset to draft status');
      await fetchData();
      scrollToTimesheet(timesheetId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset verification');
    }
  };

  // Open edit details dialog
  const openEditDetailsDialog = (timesheet) => {
    setEditingTimesheetDetails(timesheet);
    setEditDetailsForm({
      company_name: timesheet.company_name || '',
      location: timesheet.location || '',
      employee_name: timesheet.employee_name || ''
    });
    setShowEditDetailsDialog(true);
  };

  // Save timesheet details (company, location, name)
  const handleSaveDetails = async () => {
    if (!editDetailsForm.company_name || !editDetailsForm.location || !editDetailsForm.employee_name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/timesheets-v2/${editingTimesheetDetails.id}/details`, {
        company_name: editDetailsForm.company_name,
        location: editDetailsForm.location,
        employee_name: editDetailsForm.employee_name
      });
      
      toast.success('Timesheet details updated!');
      setShowEditDetailsDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update details');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/timesheets-v2/export?period_start=${cutoffPeriod.period_start}&period_end=${cutoffPeriod.period_end}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheets_${cutoffPeriod.period_start}_${cutoffPeriod.period_end}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Timesheets exported successfully!');
    } catch (error) {
      toast.error('Failed to export timesheets');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/timesheets-v2/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import timesheets');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  // Handle ZKTeco Biometric Import
  const handleBiometricImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportingBiometric(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('period_start', cutoffPeriod.period_start);
      formData.append('period_end', cutoffPeriod.period_end);
      
      const response = await axios.post(`${API}/timeclock/import-biometric`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const data = response.data;
      if (data.errors && data.errors.length > 0) {
        toast.warning(`Imported ${data.records_processed} records with ${data.errors.length} warnings`);
      } else {
        toast.success(`Successfully imported ${data.records_processed} attendance records from ${data.employees_found} employees`);
      }
      fetchData();
    } catch (error) {
      console.error('Biometric import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import biometric data');
    } finally {
      setImportingBiometric(false);
      event.target.value = '';
    }
  };

  // Open CA deduction dialog
  const handleDeductCA = (employee) => {
    setCADeductEmployee(employee);
    // Pre-fill with suggested installment amounts
    const amounts = {};
    employee.ca_details?.forEach(ca => {
      amounts[ca.id] = ca.installment;
    });
    setCADeductAmounts(amounts);
    setShowCADeductDialog(true);
  };

  // Process CA deductions
  const handleProcessCADeductions = async () => {
    setProcessingCADeduct(true);
    try {
      const period = `${cutoffPeriod.period_start} to ${cutoffPeriod.period_end}`;
      
      for (const ca of caDeductEmployee.ca_details) {
        const amount = caDeductAmounts[ca.id] || 0;
        if (amount > 0) {
          await axios.post(`${API}/cash-advance/${ca.id}/payroll-deduction`, {
            amount: parseFloat(amount),
            notes: period
          });
        }
      }
      
      toast.success('CA deductions recorded successfully!');
      setShowCADeductDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record deductions');
    } finally {
      setProcessingCADeduct(false);
    }
  };

  const navigatePeriod = (direction) => {
    if (direction === 'prev') {
      if (viewHalf === 'first') {
        // Go to previous month's second half
        const newDate = new Date(viewDate);
        newDate.setMonth(newDate.getMonth() - 1);
        setViewDate(newDate);
        setViewHalf('second');
      } else {
        // Go to current month's first half
        setViewHalf('first');
      }
    } else {
      if (viewHalf === 'second') {
        // Go to next month's first half
        const newDate = new Date(viewDate);
        newDate.setMonth(newDate.getMonth() + 1);
        setViewDate(newDate);
        setViewHalf('first');
      } else {
        // Go to current month's second half
        setViewHalf('second');
      }
    }
  };

  const getEntryForDate = (timesheet, day) => {
    const dateStr = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return timesheet.entries?.find(e => e.date === dateStr);
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      verified: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700'
    };
    return colors[status] || colors.draft;
  };

  const renderGridCell = (entry, day, canClick, isOwnTimesheet = true) => {
    const isTodayCell = isToday(day);
    
    let bgClass = '';
    if (isTodayCell) bgClass = 'bg-blue-50';
    else if (!canClick) bgClass = 'bg-gray-100';
    
    return bgClass;
  };

  const renderCellContent = (entry, type) => {
    // Show status indicators: In/Out, D, A, H
    // Admin sees actual times, employees see just status
    if (type === 'in') {
      // Check for special statuses first
      if (entry?.day_off) {
        return (
          <span 
            className="text-blue-600 font-bold text-sm cursor-help" 
            title="Day Off"
          >
            D
          </span>
        );
      }
      if (entry?.is_absent) {
        return (
          <span 
            className="text-orange-600 font-bold text-sm cursor-help" 
            title="Absent"
          >
            A
          </span>
        );
      }
      if (entry?.is_half_day) {
        return (
          <span 
            className="text-purple-600 font-bold text-sm cursor-help" 
            title={`Half Day${entry?.clock_in_time ? ` (${entry.clock_in_time})` : ''}`}
          >
            H
          </span>
        );
      }
      // Show "In" if signed in - Admin sees time, employee sees just "In"
      if (entry?.clock_in) {
        if (canViewAllTimesheets && entry?.clock_in_time) {
          return (
            <span 
              className="text-green-600 font-bold text-xs cursor-help" 
              title={`Signed In at ${entry.clock_in_time}`}
            >
              In {entry.clock_in_time}
            </span>
          );
        }
        return (
          <span 
            className="text-green-600 font-bold text-sm cursor-help" 
            title={entry?.clock_in_time ? `Signed In at ${entry.clock_in_time}` : 'Signed In'}
          >
            In
          </span>
        );
      }
      return <span className="text-gray-400">-</span>;
    } else {
      // Out row - only show "Out" if signed out, otherwise empty for D/A/H
      if (entry?.day_off || entry?.is_absent || entry?.is_half_day) {
        return <span className="text-gray-300">-</span>;
      }
      if (entry?.clock_out) {
        if (canViewAllTimesheets && entry?.clock_out_time) {
          return (
            <span 
              className="text-red-600 font-bold text-xs cursor-help" 
              title={`Signed Out at ${entry.clock_out_time}`}
            >
              Out {entry.clock_out_time}
            </span>
          );
        }
        return (
          <span 
            className="text-red-600 font-bold text-sm cursor-help" 
            title={entry?.clock_out_time ? `Signed Out at ${entry.clock_out_time}` : 'Signed Out'}
          >
            Out
          </span>
        );
      }
      return <span className="text-gray-400">-</span>;
    }
  };

  // Get display day number (simple version - no cross-month handling needed)
  const getDisplayDay = (day) => {
    return day;
  };

  // Get day name (Mon, Tue, Wed, etc.) for a given day number
  const getDayName = (day) => {
    const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Check if a day is today
  const isToday = (day) => {
    const today = new Date();
    const checkDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    return checkDate.toDateString() === today.toDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-2 sm:p-4 md:p-6" data-testid="timesheet-management-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl sm:text-2xl font-bold">Timesheet Management</h1>
        
        <div className="flex gap-2">
          {/* Quick Clock In/Out Button */}
          <Button 
            onClick={() => setShowClockPopup(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="clock-btn"
          >
            <Clock className="h-4 w-4 mr-2" />
            Clock In/Out
          </Button>
        </div>
      </div>

      {/* Cut-off Period Display - Navigation only for admins */}
      <div className="flex items-center justify-between bg-white rounded-lg p-3 sm:p-4 shadow-sm">
        {canViewAllTimesheets ? (
          <Button variant="ghost" size="sm" onClick={() => navigatePeriod('prev')} data-testid="prev-period-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
        ) : (
          <div className="w-8" /> // Spacer for non-admins
        )}
        <div className="text-center">
          <h3 className="font-semibold text-base sm:text-lg">{cutoffPeriod.label}</h3>
          <p className="text-xs sm:text-sm text-gray-500">Current Cut-off Period</p>
        </div>
        {canViewAllTimesheets ? (
          <Button variant="ghost" size="sm" onClick={() => navigatePeriod('next')} data-testid="next-period-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <div className="w-8" /> // Spacer for non-admins
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-wrap w-full bg-green-500 p-1 h-auto gap-1">
          <TabsTrigger 
            value="my-timesheet" 
            data-testid="my-timesheet-tab"
            className="flex-1 min-w-[80px] text-black text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black py-2"
          >
            <Clock className="h-4 w-4 sm:mr-2 hidden sm:block" />
            <span>My Timesheet</span>
          </TabsTrigger>
          {canViewAllTimesheets && (
            <TabsTrigger 
              value="all-timesheets" 
              data-testid="all-timesheets-tab"
              className="flex-1 min-w-[80px] text-black text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black py-2"
            >
              <Users className="h-4 w-4 sm:mr-2 hidden sm:block" />
              <span>All</span>
            </TabsTrigger>
          )}
          {canViewAllTimesheets && (
            <TabsTrigger 
              value="import-export" 
              data-testid="import-export-tab"
              className="flex-1 min-w-[80px] text-black text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black py-2"
            >
              <FileSpreadsheet className="h-4 w-4 sm:mr-2 hidden sm:block" />
              <span>Import/Export</span>
            </TabsTrigger>
          )}
          {canViewAllTimesheets && (
            <TabsTrigger 
              value="reports" 
              data-testid="reports-tab"
              className="flex-1 min-w-[80px] text-black text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black py-2"
            >
              <BarChart3 className="h-4 w-4 sm:mr-2 hidden sm:block" />
              <span>Reports</span>
            </TabsTrigger>
          )}
          {/* Hours Summary Tab - visible to all */}
          <TabsTrigger 
            value="hours" 
            data-testid="hours-tab"
            className="flex-1 min-w-[80px] text-black text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black py-2"
          >
            <Clock className="h-4 w-4 sm:mr-2 hidden sm:block" />
            <span>Hours</span>
          </TabsTrigger>
          {/* Payroll Preview tab - only for admins */}
          {(username.includes('arbe') || fullName.includes('arbe') || 
            username.includes('alma') || fullName.includes('alma') ||
            username.includes('pabs') || fullName.includes('pabs') || isAdmin) && (
            <TabsTrigger 
              value="payroll-preview" 
              data-testid="payroll-preview-tab"
              className="flex-1 min-w-[80px] text-black text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-black py-2"
              onClick={() => fetchPayrollPreview()}
            >
              <DollarSign className="h-4 w-4 sm:mr-2 hidden sm:block" />
              <span>Payroll</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* My Timesheet Tab */}
        <TabsContent value="my-timesheet" className="space-y-4">
          {!currentTimesheet && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-8 text-center">
                <FileText className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Timesheet Yet</h3>
                <p className="text-slate-500 mb-4">You don't have a timesheet for this period. Create one to start tracking your attendance.</p>
                <Button 
                  onClick={() => {
                    setCreateForm({ ...createForm, name: user?.full_name || '' });
                    setShowCreateDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-timesheet-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Create My Timesheet for {cutoffPeriod.label}
                </Button>
              </CardContent>
            </Card>
          )}

          {currentTimesheet && (
            <Card data-testid="my-timesheet-card">
              <CardHeader className="pb-3 px-3 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 flex-wrap">
                    <CardTitle className="text-base sm:text-lg">{currentTimesheet.employee_name}</CardTitle>
                    <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600 flex-wrap">
                      <span>{currentTimesheet.company_name}</span>
                      <span className="text-gray-400">•</span>
                      <span>{currentTimesheet.location}</span>
                    </div>
                    {canEditTimesheets && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDetailsDialog(currentTimesheet)}
                        className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 self-start"
                        data-testid="edit-my-timesheet-details-btn"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                  <Badge className={getStatusBadge(currentTimesheet.status)}>
                    {currentTimesheet.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="px-3 sm:px-6">
                {/* Timesheet Grid */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="py-2 px-2 text-left font-medium">Date</th>
                        {cutoffPeriod.days.map(day => {
                          const isTodayCell = isToday(day);
                          return (
                            <th 
                              key={day} 
                              className={`py-1 px-1 text-center font-medium min-w-[50px] ${isTodayCell ? 'bg-blue-100 text-blue-700' : ''}`}
                            >
                              <div className="text-xs text-gray-500">{getDayName(day)}</div>
                              <div>{getDisplayDay(day)}</div>
                            </th>
                          );
                        })}
                        <th className="py-2 px-2 text-center font-medium">Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-2 font-medium text-green-600">In</td>
                        {cutoffPeriod.days.map(day => {
                          const entry = getEntryForDate(currentTimesheet, day);
                          const isTodayCell = isToday(day);
                          const canClick = isTodayCell || canEditTimesheets;
                          return (
                            <td 
                              key={day} 
                              className={`py-2 px-1 text-center ${renderGridCell(entry, day, canClick)} ${canClick ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'}`}
                              onClick={() => canClick && handleDateClick(day)}
                            >
                              {renderCellContent(entry, 'in')}
                            </td>
                          );
                        })}
                        <td className="py-2 px-2 text-center font-bold" rowSpan={2}>
                          {currentTimesheet.total_days || 0}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-2 font-medium text-red-600">Out</td>
                        {cutoffPeriod.days.map(day => {
                          const entry = getEntryForDate(currentTimesheet, day);
                          const isTodayCell = isToday(day);
                          const canClick = isTodayCell || canEditTimesheets;
                          return (
                            <td 
                              key={day} 
                              className={`py-2 px-1 text-center ${renderGridCell(entry, day, canClick)} ${canClick ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'}`}
                              onClick={() => canClick && handleDateClick(day)}
                            >
                              {renderCellContent(entry, 'out')}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* No legend needed - showing actual times only */}

                {/* Verification & Approval Section */}
                <div className="mt-6 pt-4 border-t space-y-4">
                  {/* Workflow Status Indicator */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium mb-3 block">Approval Workflow:</Label>
                    <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                      {/* Step 1: Draft */}
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          currentTimesheet.status === 'draft' 
                            ? 'bg-gray-500 text-white ring-2 ring-gray-300' 
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          1
                        </div>
                        <span className={`text-xs mt-1 ${currentTimesheet.status === 'draft' ? 'font-bold text-gray-700' : 'text-gray-500'}`}>
                          Draft
                        </span>
                      </div>
                      
                      {/* Arrow */}
                      <div className={`flex-1 h-1 mx-2 ${currentTimesheet.verified_by ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
                      
                      {/* Step 2: Verified */}
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          currentTimesheet.status === 'verified' 
                            ? 'bg-blue-500 text-white ring-2 ring-blue-300' 
                            : currentTimesheet.verified_by 
                              ? 'bg-blue-200 text-blue-700' 
                              : 'bg-gray-200 text-gray-500'
                        }`}>
                          2
                        </div>
                        <span className={`text-xs mt-1 ${currentTimesheet.verified_by ? 'font-bold text-blue-700' : 'text-gray-500'}`}>
                          Verified
                        </span>
                      </div>
                      
                      {/* Arrow */}
                      <div className={`flex-1 h-1 mx-2 ${currentTimesheet.approved_by ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                      
                      {/* Step 3: Approved */}
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                          currentTimesheet.status === 'approved' 
                            ? 'bg-green-500 text-white ring-2 ring-green-300' 
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          3
                        </div>
                        <span className={`text-xs mt-1 ${currentTimesheet.approved_by ? 'font-bold text-green-700' : 'text-gray-500'}`}>
                          Approved
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Verified By:</Label>
                      {currentTimesheet.verified_by ? (
                        <p className="text-sm text-blue-600 font-medium mt-1">{currentTimesheet.verified_by}</p>
                      ) : isVerifier ? (
                        <Select onValueChange={(v) => handleVerify(currentTimesheet.id, v)}>
                          <SelectTrigger className="mt-1" data-testid="verify-select">
                            <SelectValue placeholder="Select verifier" />
                          </SelectTrigger>
                          <SelectContent>
                            {VERIFIERS.map(v => (
                              <SelectItem key={v} value={v}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm text-gray-400 mt-1">Pending verification</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Approved By:</Label>
                      {currentTimesheet.approved_by ? (
                        <p className="text-sm text-green-600 font-medium mt-1">{currentTimesheet.approved_by}</p>
                      ) : currentTimesheet.verified_by && isApprover ? (
                        <Select onValueChange={(v) => handleApprove(currentTimesheet.id, v)}>
                          <SelectTrigger className="mt-1" data-testid="approve-select">
                            <SelectValue placeholder="Select approver" />
                          </SelectTrigger>
                          <SelectContent>
                            {APPROVERS.map(a => (
                              <SelectItem key={a} value={a}>{a}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm text-gray-400 mt-1">
                          {currentTimesheet.verified_by ? 'Pending approval' : 'Awaiting verification'}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {canManagePayslip && currentTimesheet.approved_by && (
                      <Button 
                        onClick={() => {
                          toast.info('Payslip management moved to Payslips page');
                          window.location.href = '/arbe/payslips';
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="go-to-payslips-btn"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Go to Payslips
                      </Button>
                    )}
                    
                    {/* Reset Verification Button - Only for Admin when timesheet is verified or approved */}
                    {isAdmin && (currentTimesheet.verified_by || currentTimesheet.approved_by) && (
                      <Button 
                        onClick={() => handleResetVerification(currentTimesheet.id)}
                        variant="outline"
                        className="border-orange-300 text-orange-600 hover:bg-orange-50"
                        data-testid="reset-verification-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset to Draft
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Today's Attendance Dashboard */}
          {canViewAllTimesheets && (
            <Card data-testid="today-attendance-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5" />
                  Today&apos;s Attendance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <Check className="h-4 w-4" /> Signed In
                    </h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {todayAttendance.filter(a => a.status === 'in').length === 0 ? (
                        <p className="text-sm text-gray-500">No one signed in yet</p>
                      ) : (
                        todayAttendance.filter(a => a.status === 'in').map((a, idx) => (
                          <p key={idx} className="text-sm text-green-700">{a.name}</p>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <X className="h-4 w-4" /> Not Signed In
                    </h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {todayAttendance.filter(a => a.status === 'out' || !a.status).length === 0 ? (
                        <p className="text-sm text-gray-500">Everyone is signed in</p>
                      ) : (
                        todayAttendance.filter(a => a.status === 'out' || !a.status).map((a, idx) => (
                          <p key={idx} className="text-sm text-red-700">{a.name}</p>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Timesheets Tab */}
        {canViewAllTimesheets && (
          <TabsContent value="all-timesheets" className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-lg">All Staff Timesheets - {cutoffPeriod.label}</CardTitle>
                  {/* Search Bar */}
                  <div className="relative w-full sm:w-64">
                    <Input
                      type="text"
                      placeholder="Search employee name..."
                      value={timesheetSearch}
                      onChange={(e) => setTimesheetSearch(e.target.value)}
                      className="pl-9"
                      data-testid="timesheet-search"
                    />
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {timesheets.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No timesheets for this period</p>
                    <p className="text-sm text-gray-400 mb-4">Go to "My Timesheet" tab to create your timesheet</p>
                    {canEditTimesheets && (
                      <Button 
                        onClick={() => {
                          setCreateForm({ company_name: '', location: '', name: '' });
                          setShowCreateDialog(true);
                        }}
                        variant="outline"
                        className="mt-2"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Create Timesheet for Staff
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timesheets
                      .filter(ts => !timesheetSearch || ts.employee_name?.toLowerCase().includes(timesheetSearch.toLowerCase()))
                      .map(timesheet => (
                      <div 
                        key={timesheet.id} 
                        className={`border rounded-lg p-4 transition-all ${selectedTimesheetId === timesheet.id ? 'ring-2 ring-blue-400 bg-blue-50/30' : ''}`}
                        data-timesheet-id={timesheet.id}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold">{timesheet.employee_name}</h4>
                            <span className="text-sm text-gray-400">|</span>
                            <span className="text-sm text-gray-600">{timesheet.company_name}</span>
                            <span className="text-sm text-gray-400">•</span>
                            <span className="text-sm text-gray-600">{timesheet.location}</span>
                            {canEditTimesheets && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditDetailsDialog(timesheet)}
                                className="h-5 px-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-timesheet-details-${timesheet.id}`}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                          <Badge className={getStatusBadge(timesheet.status)}>
                            {timesheet.status}
                          </Badge>
                        </div>

                        {/* Cash Advance Summary - Original / Deducted / Remaining */}
                        {(() => {
                          const ca = caBalances[timesheet.user_id];
                          if (!ca || (ca.total_original || 0) === 0) return null;
                          const original = ca.total_original || 0;
                          const deducted = ca.total_deducted || 0;
                          const remaining = ca.total_ca_balance || 0;
                          return (
                            <div
                              className="mb-3 grid grid-cols-3 gap-2 p-2 rounded-md border border-amber-200 bg-amber-50/60 text-xs"
                              data-testid={`ca-summary-${timesheet.id}`}
                            >
                              <div className="text-center">
                                <div className="text-[10px] uppercase tracking-wide text-amber-700">CA Original</div>
                                <div className="font-semibold text-amber-900">₱{original.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-[10px] uppercase tracking-wide text-red-700">Deducted</div>
                                <div className="font-semibold text-red-700">- ₱{deducted.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-[10px] uppercase tracking-wide text-emerald-700">Remaining</div>
                                <div className={`font-semibold ${remaining > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                                  ₱{remaining.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Mini Grid */}
                        <div className="overflow-x-auto mb-3">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b">
                                <th className="py-1 px-1 text-left">Date</th>
                                {cutoffPeriod.days.map(day => (
                                  <th key={day} className="py-1 px-1 text-center min-w-[30px]">
                                    <div className="text-[10px] text-gray-400">{getDayName(day).charAt(0)}</div>
                                    <div>{getDisplayDay(day)}</div>
                                  </th>
                                ))}
                                <th className="py-1 px-1 text-center">Days</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b">
                                <td className="py-1 px-1 text-green-600">In</td>
                                {cutoffPeriod.days.map(day => {
                                  const entry = getEntryForDate(timesheet, day);
                                  return (
                                    <td 
                                      key={day} 
                                      className={`py-1 px-1 text-center ${canEditTimesheets ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                                      onClick={() => canEditTimesheets && handleDateClick(day, timesheet)}
                                    >
                                      {renderCellContent(entry, 'in')}
                                    </td>
                                  );
                                })}
                                <td className="py-1 px-1 text-center font-bold" rowSpan={2}>
                                  {timesheet.total_days || 0}
                                </td>
                              </tr>
                              <tr>
                                <td className="py-1 px-1 text-red-600">Out</td>
                                {cutoffPeriod.days.map(day => {
                                  const entry = getEntryForDate(timesheet, day);
                                  return (
                                    <td 
                                      key={day} 
                                      className={`py-1 px-1 text-center ${canEditTimesheets ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                                      onClick={() => canEditTimesheets && handleDateClick(day, timesheet)}
                                    >
                                      {renderCellContent(entry, 'out')}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2 items-center text-sm">
                          <span className="text-gray-500">Verified: {timesheet.verified_by || 'Pending'}</span>
                          <span className="text-gray-300">|</span>
                          <span className="text-gray-500">Approved: {timesheet.approved_by || 'Pending'}</span>
                          
                          {!timesheet.verified_by && isVerifier && (
                            <Select onValueChange={(v) => handleVerify(timesheet.id, v)}>
                              <SelectTrigger className="h-8 w-32">
                                <SelectValue placeholder="Verify" />
                              </SelectTrigger>
                              <SelectContent>
                                {VERIFIERS.map(v => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          
                          {timesheet.verified_by && !timesheet.approved_by && isApprover && (
                            <Select onValueChange={(v) => handleApprove(timesheet.id, v)}>
                              <SelectTrigger className="h-8 w-32">
                                <SelectValue placeholder="Approve" />
                              </SelectTrigger>
                              <SelectContent>
                                {APPROVERS.map(a => (
                                  <SelectItem key={a} value={a}>{a}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          
                          {canManagePayslip && timesheet.approved_by && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                toast.info('Payslip management moved to Payslips page');
                                window.location.href = '/arbe/payslips';
                              }}
                              className="h-8"
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              Payslips
                            </Button>
                          )}
                          
                          {/* Reset Button for Admin */}
                          {isAdmin && (timesheet.verified_by || timesheet.approved_by) && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleResetVerification(timesheet.id)}
                              className="h-8 border-orange-300 text-orange-600 hover:bg-orange-50"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Reset
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Import/Export Tab */}
        {canViewAllTimesheets && (
          <TabsContent value="import-export" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Import/Export Timesheets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Export Section */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Export Timesheets
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Download all timesheets for {cutoffPeriod.label} as an Excel file with daily attendance data.
                  </p>
                  <Button onClick={handleExport} disabled={exporting} data-testid="export-btn">
                    {exporting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export to Excel
                  </Button>
                </div>

                {/* Import Section */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    Import Timesheets
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Import timesheet data from Excel. Supports creating new timesheets and updating existing ones.
                  </p>
                  {/* Status codes legend removed - only showing actual times */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/timesheets-v2/import-template?period_start=${cutoffPeriod.period_start}&period_end=${cutoffPeriod.period_end}`, '_blank');
                      }}
                      data-testid="download-template-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Template
                    </Button>
                    <div className="flex items-center gap-2">
                      <Input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleImport}
                        disabled={importing}
                        className="max-w-xs"
                        data-testid="import-input"
                      />
                      {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                    </div>
                  </div>
                </div>

                {/* ZKTeco Biometric Import Section */}
                <div className="border-2 border-amber-300 rounded-lg p-4 bg-amber-50">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-800">
                    <Timer className="h-4 w-4" />
                    Import from ZKTeco Biometric Machine
                  </h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Import attendance data exported from your ZKTeco K40/K50 biometric machine. 
                    Export the attendance log from your ZKTeco device as CSV or Excel file.
                  </p>
                  <div className="bg-white rounded p-3 mb-3 text-xs text-gray-600">
                    <p className="font-medium mb-1">How to export from ZKTeco:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Insert USB flash drive into ZKTeco machine</li>
                      <li>Go to Menu → USB Manager → Download Attendance</li>
                      <li>Copy the file to your computer</li>
                      <li>Upload the file below</li>
                    </ol>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept=".csv,.dat,.xlsx,.xls,.txt"
                      onChange={handleBiometricImport}
                      disabled={importingBiometric}
                      className="max-w-xs"
                      data-testid="biometric-import-input"
                    />
                    {importingBiometric && <Loader2 className="h-4 w-4 animate-spin text-amber-600" />}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Supported formats: .csv, .dat, .xlsx, .txt (ZKTeco attendance log format)
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Reports Tab */}
        {canViewAllTimesheets && (
          <TabsContent value="reports" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Attendance Report - {cutoffPeriod.label}
                </CardTitle>
                {reportData.report.length > 0 && (
                  <Button 
                    onClick={() => window.print()} 
                    variant="outline" 
                    size="sm"
                    data-testid="print-report-btn"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print Report
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loadingReport ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : reportData.report.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No data for this period</p>
                ) : (
                  <div className="overflow-x-auto" id="attendance-report-table">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="py-3 px-4 text-left font-semibold">Employee</th>
                          <th className="py-3 px-4 text-left font-semibold">Company</th>
                          <th className="py-3 px-4 text-center font-semibold text-green-600">Present</th>
                          <th className="py-3 px-4 text-center font-semibold text-purple-600">Half Day</th>
                          <th className="py-3 px-4 text-center font-semibold text-amber-600">Day Off</th>
                          <th className="py-3 px-4 text-center font-semibold text-red-600">Absent</th>
                          <th className="py-3 px-4 text-center font-semibold">Total Days</th>
                          {reportData.can_see_late && (
                            <th className="py-3 px-4 text-center font-semibold text-orange-600">Late</th>
                          )}
                          <th className="py-3 px-4 text-center font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.report.map((r, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{r.employee_name}</td>
                            <td className="py-3 px-4 text-gray-600">{r.company_name}</td>
                            <td className="py-3 px-4 text-center text-green-600 font-medium">{r.present_days}</td>
                            <td className="py-3 px-4 text-center text-purple-600 font-medium">{r.half_days}</td>
                            <td className="py-3 px-4 text-center text-amber-600 font-medium">{r.day_offs}</td>
                            <td className="py-3 px-4 text-center text-red-600 font-medium">{r.absents}</td>
                            <td className="py-3 px-4 text-center font-bold">{r.total_days}</td>
                            {reportData.can_see_late && (
                              <td className="py-3 px-4 text-center text-orange-600 font-medium">{r.late_arrivals || 0}</td>
                            )}
                            <td className="py-3 px-4 text-center">
                              <Badge className={getStatusBadge(r.status)}>{r.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Hours Summary Tab */}
        <TabsContent value="hours" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Hours Summary - {cutoffPeriod.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* My Hours (for all users) */}
              {hoursSummary.filter(h => h.user_id === user?.id).map(summary => (
                <div key={summary.user_id} className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-3">My Hours This Period</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-slate-800">{summary.days_worked}</p>
                      <p className="text-xs text-slate-500">Days Worked</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{summary.total_regular_hours}h</p>
                      <p className="text-xs text-slate-500">Regular Hours</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{summary.total_overtime_hours}h</p>
                      <p className="text-xs text-slate-500">Total Overtime</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">{summary.approved_overtime_hours}h</p>
                      <p className="text-xs text-slate-500">Approved OT</p>
                    </div>
                  </div>
                  
                  {/* Deductions Section */}
                  {(summary.absent_days > 0 || summary.half_days > 0) && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                      <h4 className="font-semibold text-red-700 mb-2 text-sm">Deductions</h4>
                      <div className="grid grid-cols-3 gap-3">
                        {summary.absent_days > 0 && (
                          <div className="text-center">
                            <p className="text-xl font-bold text-orange-600">{summary.absent_days}</p>
                            <p className="text-xs text-slate-500">Absent (A)</p>
                          </div>
                        )}
                        {summary.half_days > 0 && (
                          <div className="text-center">
                            <p className="text-xl font-bold text-purple-600">{summary.half_days}</p>
                            <p className="text-xs text-slate-500">Half Days (H)</p>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="text-xl font-bold text-red-600">{summary.total_deductions}</p>
                          <p className="text-xs text-slate-500">Total Deduction (days)</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {summary.pending_overtime_hours > 0 && (
                    <p className="mt-3 text-sm text-amber-700">
                      ⏳ {summary.pending_overtime_hours} hours overtime pending approval
                    </p>
                  )}
                </div>
              ))}

              {/* All Employees Hours (for admin) */}
              {canViewAllTimesheets && hoursSummary.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">All Employees Hours</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="py-2 px-3 text-left font-semibold">Employee</th>
                          <th className="py-2 px-3 text-center font-semibold">Days</th>
                          <th className="py-2 px-3 text-center font-semibold text-orange-600">A</th>
                          <th className="py-2 px-3 text-center font-semibold text-purple-600">H</th>
                          <th className="py-2 px-3 text-center font-semibold text-red-600">Ded.</th>
                          <th className="py-2 px-3 text-center font-semibold">Regular</th>
                          <th className="py-2 px-3 text-center font-semibold">Overtime</th>
                          <th className="py-2 px-3 text-center font-semibold">Approved OT</th>
                          <th className="py-2 px-3 text-center font-semibold">Pending OT</th>
                          {/* Payment columns for admin (Arbe) */}
                          {(username.includes('arbe') || fullName.includes('arbe') || isAdmin) && (
                            <>
                              <th className="py-2 px-3 text-right font-semibold bg-green-50">Basic Pay</th>
                              <th className="py-2 px-3 text-right font-semibold bg-blue-50">OT Pay</th>
                              <th className="py-2 px-3 text-right font-semibold bg-emerald-50">Est. Total</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {hoursSummary.map(summary => (
                          <tr key={summary.user_id} className="border-b hover:bg-slate-50">
                            <td className="py-2 px-3">
                              {summary.user_name}
                              {summary.salary_type === 'daily' && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1 rounded">Daily</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">{summary.days_worked}</td>
                            <td className="py-2 px-3 text-center">
                              {summary.absent_days > 0 ? (
                                <span className="text-orange-600 font-medium">{summary.absent_days}</span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {summary.half_days > 0 ? (
                                <span className="text-purple-600 font-medium">{summary.half_days}</span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {summary.total_deductions > 0 ? (
                                <span className="text-red-600 font-bold">{summary.total_deductions}</span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center text-green-600 font-medium">{summary.total_regular_hours}h</td>
                            <td className="py-2 px-3 text-center text-amber-600 font-medium">{summary.total_overtime_hours}h</td>
                            <td className="py-2 px-3 text-center text-emerald-600 font-medium">{summary.approved_overtime_hours}h</td>
                            <td className="py-2 px-3 text-center">
                              {summary.pending_overtime_hours > 0 ? (
                                <span className="text-amber-600 font-medium">{summary.pending_overtime_hours}h ⏳</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            {/* Payment columns for admin (Arbe) */}
                            {(username.includes('arbe') || fullName.includes('arbe') || isAdmin) && (
                              <>
                                <td className="py-2 px-3 text-right bg-green-50 font-medium">
                                  {summary.estimated_basic_pay ? (
                                    <span className="text-green-700">₱{summary.estimated_basic_pay.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-right bg-blue-50 font-medium">
                                  {summary.estimated_overtime_pay ? (
                                    <span className="text-blue-700">₱{summary.estimated_overtime_pay.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-right bg-emerald-50 font-bold">
                                  {summary.estimated_total_pay ? (
                                    <span className="text-emerald-700">₱{summary.estimated_total_pay.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      {/* Totals row for admin */}
                      {(username.includes('arbe') || fullName.includes('arbe') || isAdmin) && hoursSummary.length > 0 && (
                        <tfoot>
                          <tr className="border-t-2 border-slate-300 bg-slate-100 font-bold">
                            <td className="py-3 px-3">TOTALS</td>
                            <td className="py-3 px-3 text-center">{hoursSummary.reduce((sum, s) => sum + s.days_worked, 0)}</td>
                            <td className="py-3 px-3 text-center text-green-700">{hoursSummary.reduce((sum, s) => sum + s.total_regular_hours, 0).toFixed(1)}h</td>
                            <td className="py-3 px-3 text-center text-amber-700">{hoursSummary.reduce((sum, s) => sum + s.total_overtime_hours, 0).toFixed(1)}h</td>
                            <td className="py-3 px-3 text-center text-emerald-700">{hoursSummary.reduce((sum, s) => sum + s.approved_overtime_hours, 0).toFixed(1)}h</td>
                            <td className="py-3 px-3 text-center text-amber-700">{hoursSummary.reduce((sum, s) => sum + s.pending_overtime_hours, 0).toFixed(1)}h</td>
                            <td className="py-3 px-3 text-right bg-green-100 text-green-800">
                              ₱{hoursSummary.reduce((sum, s) => sum + (s.estimated_basic_pay || 0), 0).toLocaleString()}
                            </td>
                            <td className="py-3 px-3 text-right bg-blue-100 text-blue-800">
                              ₱{hoursSummary.reduce((sum, s) => sum + (s.estimated_overtime_pay || 0), 0).toLocaleString()}
                            </td>
                            <td className="py-3 px-3 text-right bg-emerald-100 text-emerald-800">
                              ₱{hoursSummary.reduce((sum, s) => sum + (s.estimated_total_pay || 0), 0).toLocaleString()}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {/* Pending Overtime Approval Section */}
              {pendingOvertime.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold text-orange-700 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-500 text-white text-sm font-bold rounded-full">
                      {pendingOvertime.length}
                    </span>
                    Pending Overtime Approval
                  </h3>
                  <div className="space-y-2">
                    {pendingOvertime.map(ot => (
                      <div key={ot.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <div>
                          <span className="font-semibold">{ot.user_name}</span>
                          <span className="text-slate-500 mx-2">|</span>
                          <span>{ot.date}</span>
                          <span className="text-slate-500 mx-2">|</span>
                          <span className="text-orange-600 font-medium">{ot.overtime_hours}h overtime</span>
                          <p className="text-xs text-slate-500">
                            Worked {ot.clock_in_time} - {ot.clock_out_time} ({ot.total_hours}h total, {ot.regular_hours}h regular)
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/timeclock/overtime/${ot.id}/approve`, { approved: false, notes: 'Not approved' });
                                toast.success('Overtime rejected');
                                fetchData();
                              } catch (err) {
                                toast.error('Failed to reject');
                              }
                            }}
                          >
                            <X size={14} className="mr-1" /> Reject
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/timeclock/overtime/${ot.id}/approve`, { approved: true, approved_hours: ot.overtime_hours });
                                toast.success('Overtime approved!');
                                fetchData();
                              } catch (err) {
                                toast.error('Failed to approve');
                              }
                            }}
                          >
                            <Check size={14} className="mr-1" /> Approve {ot.overtime_hours}h
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Actions */}
              {canEditTimesheets && (
                <div className="mt-6 pt-4 border-t flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/timeclock/calculate-overtime`);
                        toast.success('Overtime calculated for today');
                        fetchData();
                      } catch (err) {
                        toast.error('Failed to calculate overtime');
                      }
                    }}
                  >
                    Calculate Today's Overtime
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/timeclock/cutoff-notification`);
                        toast.success('Cut-off notification sent to Alma & Jelyn');
                      } catch (err) {
                        toast.error('Failed to send notification');
                      }
                    }}
                  >
                    📋 Send Cut-off Review Notification
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Preview Tab - Admin Only */}
        <TabsContent value="payroll-preview" className="space-y-4">
          <Card>
            <CardHeader className="bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payroll Preview - {cutoffPeriod.label}
              </CardTitle>
              <p className="text-emerald-100 text-sm mt-1">
                Estimated payroll costs before generating payslips
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              {loadingPayrollPreview ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                  <span className="ml-2 text-slate-600">Loading payroll preview...</span>
                </div>
              ) : !payrollPreview ? (
                <div className="text-center py-12">
                  <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Click the Payroll tab to load preview data</p>
                  <Button 
                    onClick={fetchPayrollPreview} 
                    className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="load-payroll-preview-btn"
                  >
                    Load Payroll Preview
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <p className="text-blue-600 text-xs font-medium uppercase">Employees</p>
                      <p className="text-2xl font-bold text-blue-800">{payrollPreview.totals?.total_employees || 0}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <p className="text-emerald-600 text-xs font-medium uppercase">Total Gross</p>
                      <p className="text-2xl font-bold text-emerald-800">₱{(payrollPreview.totals?.total_gross_pay || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-amber-600 text-xs font-medium uppercase">Total Deductions</p>
                      <p className="text-2xl font-bold text-amber-800">₱{(payrollPreview.totals?.total_deductions || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                      <p className="text-green-600 text-xs font-medium uppercase">Total Net Pay</p>
                      <p className="text-2xl font-bold text-green-800">₱{(payrollPreview.totals?.total_net_pay || 0).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Detailed Breakdown */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Payroll Breakdown
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500">Basic Pay</p>
                        <p className="font-semibold text-slate-800">₱{(payrollPreview.totals?.total_basic_pay || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Overtime ({payrollPreview.totals?.total_overtime_hours || 0}h)</p>
                        <p className="font-semibold text-slate-800">₱{(payrollPreview.totals?.total_overtime_pay || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">SSS</p>
                        <p className="font-semibold text-red-600">-₱{(payrollPreview.totals?.total_sss || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">PhilHealth</p>
                        <p className="font-semibold text-red-600">-₱{(payrollPreview.totals?.total_philhealth || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Pag-IBIG</p>
                        <p className="font-semibold text-red-600">-₱{(payrollPreview.totals?.total_pagibig || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Cash Advance</p>
                        <p className="font-semibold text-red-600">-₱{(payrollPreview.totals?.total_ca_deduction || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Employee Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse" data-testid="payroll-preview-table">
                      <thead>
                        <tr className="border-b-2 border-slate-300 bg-slate-100">
                          <th className="py-3 px-3 text-left font-semibold">Employee</th>
                          <th className="py-3 px-2 text-center font-semibold">Type</th>
                          <th className="py-3 px-2 text-center font-semibold">Days</th>
                          <th className="py-3 px-3 text-right font-semibold bg-emerald-50">Basic</th>
                          <th className="py-3 px-2 text-center font-semibold">OT Hrs</th>
                          <th className="py-3 px-3 text-right font-semibold bg-blue-50">OT Pay</th>
                          <th className="py-3 px-3 text-right font-semibold bg-green-50">Gross</th>
                          <th className="py-3 px-3 text-right font-semibold bg-amber-50">Deductions</th>
                          <th className="py-3 px-3 text-right font-semibold bg-emerald-100">Net Pay</th>
                          <th className="py-3 px-2 text-center font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollPreview.employees?.map((emp, idx) => (
                          <tr key={emp.user_id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-emerald-50 transition-colors`}>
                            <td className="py-3 px-3 font-medium">
                              {emp.employee_name}
                              {emp.has_payslip && (
                                <Badge variant="outline" className="ml-2 text-xs bg-green-50 text-green-700 border-green-300">
                                  {emp.payslip_status}
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-2 text-center">
                              <Badge variant="outline" className={emp.salary_type === 'daily' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}>
                                {emp.salary_type === 'daily' ? 'Daily' : 'Monthly'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-center font-medium">{emp.days_worked}</td>
                            <td className="py-3 px-3 text-right bg-emerald-50 font-medium text-emerald-700">
                              ₱{emp.basic_pay.toLocaleString()}
                            </td>
                            <td className="py-3 px-2 text-center">
                              {emp.approved_overtime_hours > 0 ? (
                                <span className="text-amber-600 font-medium">{emp.approved_overtime_hours}h</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right bg-blue-50 font-medium text-blue-700">
                              {emp.overtime_pay > 0 ? `₱${emp.overtime_pay.toLocaleString()}` : '-'}
                            </td>
                            <td className="py-3 px-3 text-right bg-green-50 font-bold text-green-700">
                              ₱{emp.gross_pay.toLocaleString()}
                            </td>
                            <td className="py-3 px-3 text-right bg-amber-50 text-amber-700">
                              {emp.total_deductions > 0 ? `-₱${emp.total_deductions.toLocaleString()}` : '-'}
                            </td>
                            <td className="py-3 px-3 text-right bg-emerald-100 font-bold text-emerald-800">
                              ₱{emp.net_pay.toLocaleString()}
                            </td>
                            <td className="py-3 px-2 text-center">
                              {emp.has_payslip ? (
                                <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                              ) : (
                                <span className="text-slate-400 text-xs">Pending</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-slate-400 bg-slate-200 font-bold">
                          <td className="py-3 px-3" colSpan="2">TOTALS</td>
                          <td className="py-3 px-2 text-center">{payrollPreview.totals?.total_days_worked || 0}</td>
                          <td className="py-3 px-3 text-right bg-emerald-100 text-emerald-800">
                            ₱{(payrollPreview.totals?.total_basic_pay || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-2 text-center text-amber-700">{payrollPreview.totals?.total_overtime_hours || 0}h</td>
                          <td className="py-3 px-3 text-right bg-blue-100 text-blue-800">
                            ₱{(payrollPreview.totals?.total_overtime_pay || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-right bg-green-100 text-green-800">
                            ₱{(payrollPreview.totals?.total_gross_pay || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-right bg-amber-100 text-amber-800">
                            -₱{(payrollPreview.totals?.total_deductions || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-right bg-emerald-200 text-emerald-900">
                            ₱{(payrollPreview.totals?.total_net_pay || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Refresh Button */}
                  <div className="flex justify-end">
                    <Button 
                      onClick={fetchPayrollPreview} 
                      variant="outline"
                      className="gap-2"
                      disabled={loadingPayrollPreview}
                      data-testid="refresh-payroll-preview-btn"
                    >
                      <RotateCcw className={`h-4 w-4 ${loadingPayrollPreview ? 'animate-spin' : ''}`} />
                      Refresh Preview
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Print View for Attendance Report */}
      <div className="hidden print:block print-only">
        <div className="p-8 max-w-4xl mx-auto bg-white">
          <div className="border-b-2 border-slate-900 pb-4 mb-6">
            <h1 className="text-2xl font-bold text-slate-900">ATTENDANCE REPORT</h1>
            <p className="text-slate-600 mt-1">Period: {cutoffPeriod.label}</p>
            <p className="text-sm text-slate-500">Generated: {new Date().toLocaleDateString()}</p>
          </div>
          
          <table className="w-full text-sm border-collapse border border-slate-300">
            <thead>
              <tr className="bg-slate-100">
                <th className="border border-slate-300 py-2 px-3 text-left">Employee</th>
                <th className="border border-slate-300 py-2 px-3 text-left">Company</th>
                <th className="border border-slate-300 py-2 px-3 text-center">Present</th>
                <th className="border border-slate-300 py-2 px-3 text-center">Half Day</th>
                <th className="border border-slate-300 py-2 px-3 text-center">Day Off</th>
                <th className="border border-slate-300 py-2 px-3 text-center">Absent</th>
                <th className="border border-slate-300 py-2 px-3 text-center">Total</th>
                {reportData.can_see_late && (
                  <th className="border border-slate-300 py-2 px-3 text-center">Late</th>
                )}
                <th className="border border-slate-300 py-2 px-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {reportData.report.map((r, idx) => (
                <tr key={idx}>
                  <td className="border border-slate-300 py-2 px-3 font-medium">{r.employee_name}</td>
                  <td className="border border-slate-300 py-2 px-3">{r.company_name}</td>
                  <td className="border border-slate-300 py-2 px-3 text-center">{r.present_days}</td>
                  <td className="border border-slate-300 py-2 px-3 text-center">{r.half_days}</td>
                  <td className="border border-slate-300 py-2 px-3 text-center">{r.day_offs}</td>
                  <td className="border border-slate-300 py-2 px-3 text-center">{r.absents}</td>
                  <td className="border border-slate-300 py-2 px-3 text-center font-bold">{r.total_days}</td>
                  {reportData.can_see_late && (
                    <td className="border border-slate-300 py-2 px-3 text-center">{r.late_arrivals || 0}</td>
                  )}
                  <td className="border border-slate-300 py-2 px-3 text-center">{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="mt-8 pt-4 border-t">
            <p className="text-sm text-slate-600">Legend: Present = Full day worked | Half Day = 0.5 days | Day Off = Scheduled off | Absent = Unexcused absence</p>
          </div>
        </div>
      </div>

      {/* Create Timesheet Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-timesheet-dialog">
          <DialogHeader>
            <DialogTitle>Create Timesheet</DialogTitle>
            <DialogDescription>
              Create your timesheet for {cutoffPeriod.label}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Company Name *</Label>
              <Select 
                value={createForm.company_name} 
                onValueChange={(v) => setCreateForm({ ...createForm, company_name: v })}
              >
                <SelectTrigger data-testid="company-select">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Location *</Label>
              <Input
                placeholder="Enter location"
                value={createForm.location}
                onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                data-testid="location-input"
              />
            </div>
            
            <div>
              <Label>Your Name *</Label>
              <Input
                placeholder="Enter your name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                data-testid="name-input"
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <strong>Cut-off Period:</strong> {cutoffPeriod.label}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTimesheet} disabled={creating} data-testid="submit-create-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Timesheet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Status Dialog - Choose In, Out, D, A, or H - Available to ALL employees */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent data-testid="sign-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark Attendance - {selectedDate}</DialogTitle>
            <DialogDescription>
              Select a status for this date (available to all employees)
            </DialogDescription>
          </DialogHeader>
          
          {/* Show current status if any */}
          {currentEntryStatus && (currentEntryStatus.hasIn || currentEntryStatus.hasOut || currentEntryStatus.isDayOff || currentEntryStatus.isAbsent || currentEntryStatus.isHalfDay) && (
            <div className="bg-slate-100 rounded-lg p-2 text-center text-sm">
              <span className="text-slate-600">Current: </span>
              {currentEntryStatus.isDayOff && <span className="font-bold text-blue-600">D (Day Off)</span>}
              {currentEntryStatus.isAbsent && <span className="font-bold text-orange-600">A (Absent)</span>}
              {currentEntryStatus.isHalfDay && <span className="font-bold text-purple-600">H (Half Day)</span>}
              {currentEntryStatus.hasIn && !currentEntryStatus.hasOut && !currentEntryStatus.isDayOff && !currentEntryStatus.isAbsent && !currentEntryStatus.isHalfDay && (
                <span className="font-bold text-green-600">In</span>
              )}
              {currentEntryStatus.hasIn && currentEntryStatus.hasOut && !currentEntryStatus.isDayOff && !currentEntryStatus.isAbsent && !currentEntryStatus.isHalfDay && (
                <span className="font-bold text-green-600">In / <span className="text-red-600">Out</span></span>
              )}
            </div>
          )}
          
          {/* Notes/Remarks Field - Simple space to write work done or overtime reason */}
          <div className="py-2">
            <Label htmlFor="entry-notes" className="text-sm font-medium">
              Notes / Remarks (What did you do? Overtime reason?)
            </Label>
            <Textarea
              id="entry-notes"
              placeholder="e.g., Finished inventory count, worked 2 hours overtime for project deadline..."
              value={entryNotes}
              onChange={(e) => setEntryNotes(e.target.value)}
              className="mt-1 min-h-[80px] text-sm"
              data-testid="entry-notes-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-2 py-2">
            {/* Sign In */}
            <Button 
              onClick={() => handleSignAction('in')} 
              disabled={signing}
              className={`h-16 flex flex-col items-center justify-center ${
                currentEntryStatus?.hasIn && !currentEntryStatus?.isDayOff && !currentEntryStatus?.isAbsent && !currentEntryStatus?.isHalfDay
                  ? 'bg-green-700 ring-2 ring-green-400' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
              data-testid="sign-in-btn"
            >
              <LogIn className="h-5 w-5 mb-1" />
              <span className="text-sm font-medium">In</span>
            </Button>
            
            {/* Sign Out */}
            <Button 
              onClick={() => handleSignAction('out')} 
              disabled={signing}
              className={`h-16 flex flex-col items-center justify-center ${
                currentEntryStatus?.hasOut && !currentEntryStatus?.isDayOff && !currentEntryStatus?.isAbsent && !currentEntryStatus?.isHalfDay
                  ? 'bg-red-700 ring-2 ring-red-400' 
                  : 'bg-red-600 hover:bg-red-700'
              } text-white`}
              data-testid="sign-out-btn"
            >
              <LogOut className="h-5 w-5 mb-1" />
              <span className="text-sm font-medium">Out</span>
            </Button>
            
            {/* Day Off */}
            <Button 
              onClick={() => handleSignAction('dayoff')} 
              disabled={signing}
              variant={currentEntryStatus?.isDayOff ? "default" : "outline"}
              className={`h-16 flex flex-col items-center justify-center ${
                currentEntryStatus?.isDayOff 
                  ? 'bg-blue-600 text-white ring-2 ring-blue-400' 
                  : 'border-2 border-blue-400 text-blue-600 hover:bg-blue-50'
              }`}
              data-testid="dayoff-btn"
            >
              <span className="text-2xl font-bold">D</span>
              <span className="text-xs">Day Off</span>
            </Button>
            
            {/* Absent */}
            <Button 
              onClick={() => handleSignAction('absent')} 
              disabled={signing}
              variant={currentEntryStatus?.isAbsent ? "default" : "outline"}
              className={`h-16 flex flex-col items-center justify-center ${
                currentEntryStatus?.isAbsent 
                  ? 'bg-orange-600 text-white ring-2 ring-orange-400' 
                  : 'border-2 border-orange-400 text-orange-600 hover:bg-orange-50'
              }`}
              data-testid="absent-btn"
            >
              <span className="text-2xl font-bold">A</span>
              <span className="text-xs">Absent</span>
            </Button>
            
            {/* Half Day */}
            <Button 
              onClick={() => handleSignAction('halfday')} 
              disabled={signing}
              variant={currentEntryStatus?.isHalfDay ? "default" : "outline"}
              className={`h-16 flex flex-col items-center justify-center col-span-2 ${
                currentEntryStatus?.isHalfDay 
                  ? 'bg-purple-600 text-white ring-2 ring-purple-400' 
                  : 'border-2 border-purple-400 text-purple-600 hover:bg-purple-50'
              }`}
              data-testid="halfday-btn"
            >
              <span className="text-2xl font-bold">H</span>
              <span className="text-xs">Half Day</span>
            </Button>
          </div>
          
          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="entry-notes">Notes (Optional)</Label>
            <Textarea
              id="entry-notes"
              placeholder="Add notes about your work, overtime reason, or any other details..."
              value={entryNotes}
              onChange={(e) => setEntryNotes(e.target.value)}
              className="min-h-[80px]"
              data-testid="entry-notes-input"
            />
            <p className="text-xs text-slate-500">
              Use this field to document work done, overtime reasons, or any other relevant information.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)} className="w-full">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Edit Time Entry Dialog */}
      <Dialog open={showAdminEditDialog} onOpenChange={setShowAdminEditDialog}>
        <DialogContent data-testid="admin-edit-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Attendance Status
            </DialogTitle>
            <DialogDescription>
              {editingTimesheet?.employee_name} - {editingDate}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            {/* Show current recorded times if any */}
            {(editingEntry.clock_in_time || editingEntry.clock_out_time) && !editingEntry.day_off && !editingEntry.absent && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">Recorded Times:</p>
                <div className="flex gap-4 text-sm">
                  {editingEntry.clock_in_time && (
                    <span className="text-green-700">
                      <strong>In:</strong> {editingEntry.clock_in_time}
                    </span>
                  )}
                  {editingEntry.clock_out_time && (
                    <span className="text-red-700">
                      <strong>Out:</strong> {editingEntry.clock_out_time}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* In/Out Status */}
            <div className="p-3 border rounded-lg bg-slate-50">
              <label className="block text-sm font-medium text-slate-700 mb-3">Set Attendance Status</label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={editingEntry.clock_in_time && !editingEntry.clock_out_time && !editingEntry.day_off && !editingEntry.absent && !editingEntry.half_day ? "default" : "outline"}
                  className={editingEntry.clock_in_time && !editingEntry.clock_out_time && !editingEntry.day_off && !editingEntry.absent && !editingEntry.half_day ? "bg-green-600 hover:bg-green-700" : ""}
                  onClick={() => setEditingEntry({ 
                    ...editingEntry, 
                    clock_in_time: editingEntry.clock_in_time || '08:00',
                    clock_out_time: '',
                    day_off: false, 
                    absent: false, 
                    half_day: false 
                  })}
                >
                  <LogIn className="h-4 w-4 mr-1" /> In
                </Button>
                <Button
                  type="button"
                  variant={editingEntry.clock_in_time && editingEntry.clock_out_time && !editingEntry.day_off && !editingEntry.absent && !editingEntry.half_day ? "default" : "outline"}
                  className={editingEntry.clock_in_time && editingEntry.clock_out_time && !editingEntry.day_off && !editingEntry.absent && !editingEntry.half_day ? "bg-red-600 hover:bg-red-700" : ""}
                  onClick={() => setEditingEntry({ 
                    ...editingEntry, 
                    clock_in_time: editingEntry.clock_in_time || '08:00',
                    clock_out_time: editingEntry.clock_out_time || '17:00',
                    day_off: false, 
                    absent: false, 
                    half_day: false 
                  })}
                >
                  <LogOut className="h-4 w-4 mr-1" /> In + Out
                </Button>
              </div>
              
              {/* Time inputs for admin to edit actual times */}
              {editingEntry.clock_in_time && !editingEntry.day_off && !editingEntry.absent && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">In Time</label>
                    <Input
                      type="time"
                      value={editingEntry.clock_in_time}
                      onChange={(e) => setEditingEntry({ ...editingEntry, clock_in_time: e.target.value })}
                      className="text-sm"
                    />
                  </div>
                  {editingEntry.clock_out_time && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Out Time</label>
                      <Input
                        type="time"
                        value={editingEntry.clock_out_time}
                        onChange={(e) => setEditingEntry({ ...editingEntry, clock_out_time: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Special Status */}
            <div className="p-3 border rounded-lg">
              <label className="block text-sm font-medium text-slate-700 mb-3">Or Mark As:</label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={editingEntry.day_off ? "default" : "outline"}
                  className={editingEntry.day_off ? "bg-blue-600 hover:bg-blue-700" : "border-blue-400 text-blue-600"}
                  onClick={() => setEditingEntry({ 
                    ...editingEntry, 
                    day_off: true, 
                    absent: false, 
                    half_day: false,
                    clock_in_time: '',
                    clock_out_time: ''
                  })}
                >
                  <span className="font-bold mr-1">D</span> Day Off
                </Button>
                <Button
                  type="button"
                  variant={editingEntry.absent ? "default" : "outline"}
                  className={editingEntry.absent ? "bg-orange-600 hover:bg-orange-700" : "border-orange-400 text-orange-600"}
                  onClick={() => setEditingEntry({ 
                    ...editingEntry, 
                    absent: true, 
                    day_off: false, 
                    half_day: false,
                    clock_in_time: '',
                    clock_out_time: ''
                  })}
                >
                  <span className="font-bold mr-1">A</span> Absent
                </Button>
                <Button
                  type="button"
                  variant={editingEntry.half_day ? "default" : "outline"}
                  className={editingEntry.half_day ? "bg-purple-600 hover:bg-purple-700" : "border-purple-400 text-purple-600"}
                  onClick={() => setEditingEntry({ 
                    ...editingEntry, 
                    half_day: true, 
                    day_off: false, 
                    absent: false,
                    clock_in_time: '08:00',
                    clock_out_time: '12:00'
                  })}
                >
                  <span className="font-bold mr-1">H</span> Half Day
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="destructive" 
              onClick={handleAdminRemove}
              disabled={saving}
              className="mr-auto"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </Button>
            <Button variant="outline" onClick={() => setShowAdminEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdminEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Timesheet Details Dialog (Company, Location, Name) */}
      <Dialog open={showEditDetailsDialog} onOpenChange={setShowEditDetailsDialog}>
        <DialogContent data-testid="edit-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Timesheet Details
            </DialogTitle>
            <DialogDescription>
              Update company name, location, or employee name
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Company Name *</Label>
              <Select 
                value={editDetailsForm.company_name} 
                onValueChange={(v) => setEditDetailsForm({ ...editDetailsForm, company_name: v })}
              >
                <SelectTrigger data-testid="edit-company-select">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Location *</Label>
              <Input
                placeholder="Enter location"
                value={editDetailsForm.location}
                onChange={(e) => setEditDetailsForm({ ...editDetailsForm, location: e.target.value })}
                data-testid="edit-location-input"
              />
            </div>
            
            <div>
              <Label>Employee Name *</Label>
              <Input
                placeholder="Enter employee name"
                value={editDetailsForm.employee_name}
                onChange={(e) => setEditDetailsForm({ ...editDetailsForm, employee_name: e.target.value })}
                data-testid="edit-employee-name-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDetailsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDetails} disabled={saving} data-testid="save-details-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CA Deduction Dialog */}
      <Dialog open={showCADeductDialog} onOpenChange={setShowCADeductDialog}>
        <DialogContent className="max-w-lg" data-testid="ca-deduct-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <DollarSign className="h-5 w-5" />
              Deduct Cash Advance
            </DialogTitle>
            <DialogDescription>
              Deduct CA from {caDeductEmployee?.employee_name}&apos;s payroll for {cutoffPeriod.label}
            </DialogDescription>
          </DialogHeader>
          
          {caDeductEmployee && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Gross Pay:</span>
                  <span className="font-medium">₱{caDeductEmployee.gross_pay?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Statutory Deductions:</span>
                  <span className="font-medium text-red-600">-₱{caDeductEmployee.statutory_deductions?.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Outstanding Cash Advances:</Label>
                {caDeductEmployee.ca_details?.map((ca, idx) => (
                  <div key={ca.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">{ca.reason || `CA #${idx + 1}`}</span>
                      <span className="text-gray-500">Remaining: ₱{ca.remaining?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500 w-24">Deduct Amount:</Label>
                      <Input
                        type="number"
                        value={caDeductAmounts[ca.id] || ''}
                        onChange={(e) => setCADeductAmounts({
                          ...caDeductAmounts,
                          [ca.id]: e.target.value
                        })}
                        placeholder={`Suggested: ₱${ca.installment}`}
                        className="flex-1"
                        max={ca.remaining}
                      />
                    </div>
                    <div className="text-xs text-gray-400">
                      Suggested installment: ₱{ca.installment?.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-orange-50 rounded-lg p-3">
                <div className="flex justify-between font-semibold">
                  <span className="text-orange-700">Total CA to Deduct:</span>
                  <span className="text-orange-700">
                    ₱{Object.values(caDeductAmounts).reduce((sum, amt) => sum + (parseFloat(amt) || 0), 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-600">Estimated Net Pay:</span>
                  <span className="font-bold text-green-600">
                    ₱{(
                      caDeductEmployee.gross_pay - 
                      caDeductEmployee.statutory_deductions - 
                      Object.values(caDeductAmounts).reduce((sum, amt) => sum + (parseFloat(amt) || 0), 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCADeductDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleProcessCADeductions} 
              disabled={processingCADeduct}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {processingCADeduct ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Deductions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Clock In/Out Popup */}
      <Dialog open={showClockPopup} onOpenChange={setShowClockPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              Time & Attendance
            </DialogTitle>
            <DialogDescription>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Status */}
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-sm text-slate-600 mb-2">Today&apos;s Status:</p>
              <div className="flex justify-center gap-4">
                <div className={`px-4 py-2 rounded-lg ${todayStatus.in ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  <p className="text-xs">Sign In</p>
                  <p className="font-semibold">{todayStatus.in ? '✓ Done' : 'Pending'}</p>
                </div>
                <div className={`px-4 py-2 rounded-lg ${todayStatus.out ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                  <p className="text-xs">Sign Out</p>
                  <p className="font-semibold">{todayStatus.out ? '✓ Done' : 'Pending'}</p>
                </div>
              </div>
            </div>

            {/* No Timesheet Warning */}
            {!currentTimesheet ? (
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 text-amber-600" />
                <p className="font-semibold text-amber-800">No timesheet found</p>
                <p className="text-sm text-amber-600">Please ask your admin to create a timesheet for you.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Sign In Button */}
                <Button
                  onClick={handleQuickClockIn}
                  disabled={clockingIn || todayStatus.in}
                  className={`w-full h-12 text-base ${todayStatus.in ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                  data-testid="quick-clock-in-btn"
                >
                  {clockingIn ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <LogIn className="h-5 w-5 mr-2" />
                  )}
                  Sign In (Start Shift)
                  {todayStatus.in && <Check className="h-4 w-4 ml-2" />}
                </Button>

                {/* Sign Out Button */}
                <Button
                  onClick={handleQuickClockOut}
                  disabled={clockingOut || !todayStatus.in || todayStatus.out}
                  className={`w-full h-12 text-base ${!todayStatus.in || todayStatus.out ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="quick-clock-out-btn"
                >
                  {clockingOut ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <LogOut className="h-5 w-5 mr-2" />
                  )}
                  Sign Out (End Shift)
                  {todayStatus.out && <Check className="h-4 w-4 ml-2" />}
                </Button>

              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockPopup(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overtime Request Dialog removed - simplified to notes on entry */}
    </div>
  );
};

export default TimesheetManagement;
